<template>
  <div class="import-proposals-container is-flex">
    <b-field expanded grouped class="column-direction-touch">
      <b-field expanded id="import-proposals-field">
        <div expanded class="field">
          <b-upload expanded v-model="data.file" accept=".xlsx,.xls,.csv" @input="handleFileInput">
            <a class="button is-primary is-fullwidth" :class="{ 'is-loading': loading.import }">
              <b-icon icon="import"></b-icon>
              <span>Importar Propostas</span>
            </a>
          </b-upload>
        </div>
      </b-field>

      <b-field>
        <b-button
          expanded
          icon-left="file-download"
          type="is-primary"
          @click.prevent.stop="fetchImportProposalsModel()"
        >
          Modelo Importação
        </b-button>
      </b-field>
    </b-field>

    <b-progress
      show-value
      format="percent"
      id="import-proposals-upload-progress"
      type="is-info"
      size="is-medium"
      :value="auctionProposalsImportUpload"
    ></b-progress>

    <b-modal has-modal-card :active.sync="declarationsModal" :onCancel="cancelDeclarations">
      <DeclarationsModal
        v-if="declarationsModal"
        :showPreference="showPreferenceDeclarations"
        :companySize="company.companySize"
        @confirm="handleDeclarationsConfirm"
        @cancel="cancelDeclarations"
      />
    </b-modal>

    <b-modal has-modal-card :active.sync="validandoPIN" :onCancel="cancelPin">
      <PinForm v-if="validandoPIN" :reason="reasonPIN" @validar="onPinValidated" />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { fileServer } from '@/utils/functions';

import PinForm from '@/views/User/Pin';
import DeclarationsModal from './DeclarationsModal';

const uploadLimit = 5242880; // 5MB

export default {
  name: 'ImportAuctionProposals',
  components: {
    DeclarationsModal,
    PinForm,
  },
  data() {
    const data = {
      file: [],
    };

    const loading = {
      import: false,
    };

    return {
      data,
      loading,
      declarationsModal: false,
      pendingImport: false,
      selectedFile: null,
      validandoPIN: false,
      reasonPIN: 'PREGAO-CRIAR-PROPOSTA',
    };
  },
  computed: {
    ...mapState('proposals', ['auctionProposalsImportUpload']),
    ...mapState('companies', ['company']),
    ...mapGetters('auctionsDeclarations', {
      auctionDeclarations: 'getDeclarations',
    }),
    ...mapGetters({
      ofertaCompra: 'obterOfertaCompra',
      pinValido: 'getValidPin',
    }),
    showPreferenceDeclarations() {
      return (
        this.company.preference &&
        this.ofertaCompra.tipo_edital === 'RESERVADO' &&
        this.ofertaCompra.preferencia_regional
      );
    },
    maiorDesconto() {
      return this.ofertaCompra.tipo_negociacao && this.ofertaCompra.tipo_negociacao.id === 3;
    },
  },
  methods: {
    ...mapActions('proposals', ['importAuctionProposals', 'resetAuctionProposalsUpload']),
    ...mapActions('auctionsDeclarations', ['createAuctionDeclaration', 'removeAuctionDeclaration']),
    fetchImportProposalsModel() {
      const url = fileServer('modelo-importar-propostas.xlsx', 'files');
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    handleFileInput() {
      this.resetAuctionProposalsUpload();

      const file = Array.isArray(this.data.file) ? this.data.file[0] : this.data.file;

      if (!file) {
        return;
      }

      this.selectedFile = file;

      if (!this.pinValido) {
        this.pendingImport = true;
        this.validandoPIN = true;
        return;
      }

      this.continueImportAfterPin();
    },
    continueImportAfterPin() {
      const requirements = this.auctionDeclarations.some(
        element => element.declaration === 'REQUISITOS',
      );

      if (!requirements) {
        this.pendingImport = true;
        this.declarationsModal = true;
        return;
      }

      this.importProposals();
    },
    onPinValidated() {
      this.validandoPIN = false;
      if (this.pendingImport) {
        this.pendingImport = false;
        this.continueImportAfterPin();
      }
    },
    cancelPin() {
      this.validandoPIN = false;
      this.pendingImport = false;
      this.data.file = [];
      this.selectedFile = null;
    },
    async createDeclaration(declarationType, declarationValue) {
      try {
        if (declarationValue) {
          await this.createAuctionDeclaration({
            auctionId: this.$route.params.id,
            companyId: this.company.id,
            type: declarationType,
            value: declarationValue,
          });
        } else {
          const declaration = this.auctionDeclarations.find(
            element => element.declaration === declarationType,
          );

          if (declaration) {
            await this.removeAuctionDeclaration({
              auctionId: this.$route.params.id,
              companyId: this.company.id,
              declarationId: declaration.id,
            });
          }
        }
      } catch (error) {
        if (error && error.response) {
          const { data } = error.response;
          if (data.error === 'ValidationError' && data.details.message === 'Fora do prazo') {
            this.$alerta(data.details.message, 'is-danger');
            return;
          }
        }

        this.$alerta(
          declarationValue ? 'Erro ao aceitar declaração' : 'Erro ao remover declaração',
          'is-danger',
        );
      }
    },
    async handleDeclarationsConfirm(payload = {}) {
      await this.createDeclaration('requirements', true);

      if (payload.regionalPreference === true) {
        await this.createDeclaration('regionalPreference', true);
      }

      this.declarationsModal = false;

      // Se havia uma importação pendente, executa agora
      if (this.pendingImport) {
        this.pendingImport = false;
        this.importProposals();
      }
    },
    cancelDeclarations() {
      this.declarationsModal = false;
      this.pendingImport = false;
      this.data.file = [];
      this.selectedFile = null;
    },
    async importProposals() {
      const file =
        this.selectedFile || (Array.isArray(this.data.file) ? this.data.file[0] : this.data.file);

      if (!file) {
        return;
      }

      if (
        ![
          'text/csv',
          'text/plain',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ].includes(file.type)
      ) {
        this.$alerta('Insira um arquivo com o formato .xlsx ou .csv', 'is-danger');
        this.data.file = [];
        this.selectedFile = null;
        return;
      }

      if (file.size > uploadLimit) {
        this.$alerta('Insira um arquivo com até 5MB', 'is-danger');
        this.data.file = [];
        this.selectedFile = null;
        return;
      }

      const data = new FormData();
      data.append('proposals-import', file);

      this.$emit('mode', 'import');
      this.loading.import = true;
      try {
        await this.importAuctionProposals({ auctionId: this.$route.params.id, data });
        this.$alerta('Importação com sucesso', 'is-success');
      } catch (error) {
        if (error?.response?.data?.message === 'ValidationError') {
          this.$snackbar(error.response.data.details.message, 'is-warning', 'is-top', true);
          return;
        }
        if (error?.response?.data?.details?.type === 'InvalidPriceColumn') {
          this.$snackbar(error.response.data.details.message, 'is-warning', 'is-top', true);
          return;
        }
        this.$alerta('Erro ao realizar importação', 'is-danger');
      } finally {
        this.$emit('mode', '');
        this.loading.import = false;
        this.data.file = [];
        this.selectedFile = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#import-proposals-upload-progress {
  margin-left: 40px;
  margin-top: 10px;
  width: 100%;
}

.import-proposals-container {
  margin: 1rem 0;
}
</style>

<style lang="scss">
@media (max-width: 1023px) {
  #import-proposals-field .field {
    display: flex;
    flex-direction: column;

    .field .upload {
      width: 100%;
    }
  }

  #import-proposals-upload-progress {
    margin-left: unset;
  }

  .import-proposals-container {
    flex-direction: column;
  }
}
</style>
